import React, { useEffect, useState } from "react";
import {
    Box,
    Typography,
    Button,
    List,
    ListItem,
    Divider,
    Chip,
    TextField,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Paper,
    Grid,
    Avatar,
    ListItemAvatar,
    ListItemText,
    IconButton,
    CircularProgress,
} from "@mui/material";
import ZoomInIcon from "@mui/icons-material/ZoomIn";
import { CartType, CartItem } from "../../models/CartType"; // Adjust import path
import { useParams } from "react-router-dom";
import { useSignedInUserState } from "../../hooks/useAuthState";
import { RefundStatus, RefundType } from "../../models/refundType";
import { refundService } from "../../services/firebase/refundService";
import { CartService } from "../../services/functions/cartService";
import { OrderHistoryService } from "../../services/order/orderHistoryService";
import { MetricsNames } from "../../services/firebase/metricsService";
import { logEvent } from "../../firebase";
import { formatCurrency } from "../refundOrderInStore/refundOrderInStore";
import { MenuCartItem } from "../../models/fullMenuCart";
import { MenuItemDetails } from "../../shared/menuItemDetails";
import { FullMenuPageData } from "../../profilePage/maintainSpecificRestaurant/upsertFullMenuHelpers";
import { menuService } from "../../services/firebase/menuService";

const RefundManagementPage: React.FC = () => {
    const { refundId } = useParams<{ refundId: string }>();
    const [selectedImage, setSelectedImage] = useState<string>("");
    const [menuData, setMenuData] = useState<FullMenuPageData>({ baseItems: [], menu: { categories: [], bundles: [] } });
    const [refundAmount, setRefundAmount] = useState<number>(0);
    const [decisionComment, setDecisionComment] = useState("");
    const [refundRequest, setRefundRequest] = useState<RefundType | null>(null);
    const [cart, setCart] = useState<CartType | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [isRefundable, setIsRefundable] = useState<boolean>(false);
    const [error, setError] = useState<string>("");
    const user = useSignedInUserState().user;

    const refresh = async () => {
        const fetchedRefundRequest = await refundService.fetchRefundById(refundId ?? "");
        const fetchedCart = await OrderHistoryService.getOrderById(fetchedRefundRequest.orderId)
        if (fetchedCart) {
            const menu = await menuService.fetchFullMenuCached(fetchedCart.restaurantId);
            setMenuData(menu);
        }
        setRefundRequest(fetchedRefundRequest);
        setCart(fetchedCart);
        setIsRefundable(fetchedRefundRequest.rufundStatus == "PENDING_RESTAURANT")
    }

    useEffect(() => {
        const fetchMessages = async () => {
            try {
                if (!user || !user.uid) {
                    setError("You must be signed in to view messages.");
                    setLoading(false);
                    return;
                }
                setError("");
                setLoading(true);
                await refresh()
            } catch (err) {
                setError("Failed to fetch messages. Please try again later.");
            } finally {
                setLoading(false);
            }
        };

        fetchMessages();
    }, [user?.uid]);

    const getStatusMessage = (status: CartType["status"]) => {
        switch (status) {
            case "PAYMENT_COMPLETE":
                return "Payment Complete";
            case "RESTAURANT_ACCEPTED_ORDER":
                return "Waiting for the restaurant";
            case "PAYMENT_PENDING":
                return "Payment Pending";
            case "PREPARING_ORDER":
                return "Preparing Order";
            case "READY_FOR_COLLECTION":
                return "Ready for Collection";
            case "COLLECTED":
                return "Collected";
            case "PARTIAL_REFUND_COMPLETE":
            case "FULL_REFUND_COMPLETE":
                return "Refund Complete";
            case "REFUND_REJECTED":
                return "Refund Rejected";
            case "FULL_REFUND_PROCESSING":
                return "Full Refund Processing";
            case "PARTIAL_REFUND_PROCESSING":
                return "Partial Refund Processing";
            case "PENDING_RESTAURANT":
                return "pending refund";
            case "FAILED":
                return "Failed";
            default:
                return "Unknown Status";
        }
    };

    const getStatusChipColor = (status: CartType["status"]) => {
        switch (status) {
            case "PAYMENT_COMPLETE":
                return "#A6C48A";
            case "RESTAURANT_ACCEPTED_ORDER":
                return "A6C48A";
            case "PREPARING_ORDER":
                return "A6C48A";
            case "READY_FOR_COLLECTION":
                return "#A6C48A";
            case "COLLECTED":
                return "#162C1F";
            case "PARTIAL_REFUND_COMPLETE":
                return "#162C1F";
            case "FULL_REFUND_COMPLETE":
                return "#ffffff";
            case "FULL_REFUND_PROCESSING":
                return "#FFA07A";
            case "PARTIAL_REFUND_PROCESSING":
                return "#FFA07A";
            case "PENDING_RESTAURANT":
                return "#A6C48A";
            case "FAILED":
                return "FF6F61";
            default:
                return "default";
        }
    };

    const ViewDecision = () => {
        return <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
                Refund Decision
            </Typography>

            <TextField
                label="Decision Comments"
                multiline
                fullWidth
                rows={3}
                disabled={true}
                value={refundRequest!.refundDecision}
                sx={{ mb: 2 }}
            />
        </Paper>
    }

    const handleDecision = async (decision: RefundStatus) => {
        setLoading(true);
        if (!isRefundable) {
            setError("Already Refunded")
        }

        logEvent(MetricsNames.REFUND_PROCESSED)
        if (decision == "FULL_REFUND_PROCESSING") {
            await CartService.refundDecision({
                refundDecisionAmount: cart!.totalAmount,
                refundDecisionMessage: decisionComment,
                refundDecisionStatus: decision,
                refundRequestId: refundId,
            })
            logEvent(MetricsNames.REFUND_PROCESSED_DETAILS, {
                "refundAmount": cart!.totalAmount,
                "refundDecisionStatus": decision
            })
        } else if (decision == "PARTIAL_REFUND_PROCESSING") {
            await CartService.refundDecision({
                refundDecisionAmount: refundAmount,
                refundDecisionMessage: decisionComment,
                refundDecisionStatus: decision,
                refundRequestId: refundId,
            })
            logEvent(MetricsNames.REFUND_PROCESSED_DETAILS, {
                "refundAmount": refundAmount,
                "refundDecisionStatus": decision
            })
        } else if (decision == "REFUND_REJECTED") {
            await CartService.refundDecision({
                refundDecisionAmount: 0,
                refundDecisionMessage: decisionComment,
                refundDecisionStatus: decision,
                refundRequestId: refundId,
            })
            logEvent(MetricsNames.REFUND_PROCESSED_DETAILS, {
                "refundAmount": 0,
                "refundDecisionStatus": decision
            })
        }
        await refresh()
        setLoading(false);
    }

    const getImgUrl = (item: MenuCartItem) => {
        if (item.bundle) {
            if (item.bundle.imageUrl) {
                return item.bundle.imageUrl
            };
        }
        if (item.comboVariant) {
            if (item.comboVariant.imageUrl) {
                return item.comboVariant.imageUrl;
            }
        }
        const bi = menuData.baseItems.find(bi => bi.id == item.baseItemId);
        if (bi) {
            return bi.imageUrl
        }
        return undefined
    }

    if (loading) {
        return <Box sx={{ height: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }} className={'patlite'}>
            <CircularProgress size={40} sx={{ color: "black" }} />
        </Box>
    }

    if (error) {
        <Typography color="error">{error}</Typography>
    }

    if (!refundRequest || !cart) {
        return <Box sx={{ height: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }} className={'patlite'}>
            <CircularProgress size={40} sx={{ color: "black" }} />
        </Box>
    }

    return (
        <Box sx={{ p: 3, height: '100vh' }} className="patlite">
            <Typography variant="h4" gutterBottom>
                Refund Request Management
            </Typography>

            {/* Refund Request Details */}
            <Paper sx={{ p: 2, mb: 3 }}>
                <Typography variant="h6" gutterBottom>
                    Customer's Request
                </Typography>
                <Typography variant="body1" paragraph>
                    {refundRequest.refundMessage}
                </Typography>

                <Typography variant="subtitle1" gutterBottom>
                    Attachments:
                </Typography>
                <Grid container spacing={2}>
                    {refundRequest.refundImages.map((url, index) => (
                        <Grid item key={index}>
                            <Paper variant="outlined" sx={{ p: 1 }}>
                                <img
                                    src={url}
                                    alt={`Attachment ${index + 1}`}
                                    style={{
                                        height: 100,
                                        cursor: "pointer",
                                        objectFit: "cover"
                                    }}
                                    onClick={() => setSelectedImage(url)}
                                />
                                <IconButton
                                    size="small"
                                    onClick={() => setSelectedImage(url)}
                                >
                                    <ZoomInIcon fontSize="small" />
                                </IconButton>
                            </Paper>
                        </Grid>
                    ))}
                </Grid>
            </Paper>

            {/* Order Details */}
            <Paper sx={{ p: 2, mb: 3 }}>
                <Typography variant="h6" gutterBottom>
                    Order Details
                </Typography>

                <List>
                    {cart.items.map((item: CartItem, index) => (
                        <ListItem key={index}>
                            {getImgUrl(item.menuCartItem) &&
                                <img
                                    style={{ height: "80px", width: "80px", borderRadius: 2 }}
                                    src={getImgUrl(item.menuCartItem)}
                                    alt="Item thumbnail"
                                />
                            }


                            <MenuItemDetails item={item.menuCartItem} menuData={menuData} />
                        </ListItem>
                    ))}
                </List>

                <Divider sx={{ my: 2 }} />

                <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                    <Typography variant="h6">Total Amount:</Typography>
                    <Typography variant="h6">
                        {formatCurrency(cart.totalAmount)}
                    </Typography>
                </Box>

                <Box sx={{ mt: 2 }}>
                    <Chip
                        label={`Status: ${getStatusMessage(refundRequest.rufundStatus)}`}
                        sx={{ mr: 2, backgroundColor: getStatusChipColor(refundRequest.rufundStatus) }}
                    />
                    <Chip
                        label={`Already Refunded: ${formatCurrency(refundRequest.refundAmount ? refundRequest.refundAmount : 0)}`}
                    />
                </Box>
            </Paper>

            {/* Decision Making Section */}
            {
                isRefundable ?
                    <Paper sx={{ p: 2 }}>
                        <Typography variant="h6" gutterBottom>
                            Refund Decision
                        </Typography>

                        <TextField
                            label="Decision Comments"
                            multiline
                            fullWidth
                            rows={3}
                            value={decisionComment}
                            onChange={(e) => setDecisionComment(e.target.value)}
                            sx={{ mb: 2 }}
                        />

                        <Box sx={{ display: "flex", gap: 2, alignItems: "center", mb: 2 }}>
                            <Button
                                variant="contained"
                                sx={{ background: "#162C1F", border: "1px solid #162C1F", borderRadius: "24px" }}
                                onClick={() => handleDecision("FULL_REFUND_PROCESSING")}
                            >
                                Full Refund ({formatCurrency(cart!.totalAmount)})
                            </Button>

                            <Button
                                variant="contained"
                                color="warning"
                                onClick={() => handleDecision("PARTIAL_REFUND_PROCESSING")}
                            >
                                Partial Refund
                            </Button>

                            <TextField
                                label="Partial Amount (R)"
                                type="number"
                                value={refundAmount / 100}
                                onChange={(e) => {
                                    if (Number(e.target.value) * 100 > cart!.totalAmount) {
                                        setRefundAmount(cart!.totalAmount)
                                    } else {
                                        setRefundAmount(Number(e.target.value) * 100)
                                    }
                                }}
                                InputProps={{ inputProps: { min: 0, max: cart!.totalAmount / 100 } }}
                                sx={{ width: 150 }}
                            />
                        </Box>

                        <Button
                            variant="contained"
                            color="error"
                            onClick={() => handleDecision("REFUND_REJECTED")}
                        >
                            Deny Request
                        </Button>
                    </Paper>
                    :
                    <ViewDecision />
            }

            {/* Image Preview Dialog */}
            <Dialog
                open={!!selectedImage}
                onClose={() => setSelectedImage("")}
                maxWidth="lg"
            >
                <DialogTitle>Attachment Preview</DialogTitle>
                <DialogContent>
                    {selectedImage && (
                        <img
                            src={selectedImage}
                            alt="Full size preview"
                            style={{ width: "100%", height: "auto" }}
                        />
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setSelectedImage("")}>Close</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default RefundManagementPage;